import * as z from 'zod';
import { Button, Field, Flex, Input, Stack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const schema = z
	.object({
		firstDay: z.string().min(1, { message: 'Pole wymagane' }),
		lastDay: z.string().min(1, { message: 'Pole wymagane' }),
	})
	.superRefine((data, ctx) => {
		if (!data.firstDay || !data.lastDay) return;

		const firstDay = new Date(data.firstDay);
		const lastDay = new Date(data.lastDay);

		if (Number.isNaN(firstDay.getTime()) || Number.isNaN(lastDay.getTime())) return;

		if (lastDay < firstDay) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['firstDay'],
				message: 'Data początku nie może być późniejsza niż data końca',
			});
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['lastDay'],
				message: 'Data końca nie może być wcześniejsza niż data początku',
			});
		}
	});

export type TimeRangeFormProps = {
	onSubmit: (data: { firstDay: string; lastDay: string }) => void | Promise<void>;
	submitBtnText: string;
	defaultValues: { firstDay: string; lastDay: string };
	compact?: boolean;
} & any;

export const TimeRangeForm = ({ defaultValues, submitBtnText, onSubmit, compact = false, ...otherProps }: TimeRangeFormProps) => {
	const formDefaultValues = useMemo(() => ({
		firstDay: defaultValues?.firstDay ?? '',
		lastDay: defaultValues?.lastDay ?? '',
	}), [defaultValues?.firstDay, defaultValues?.lastDay]);

	const { handleSubmit, register, formState, reset, watch } = useForm<{
		firstDay: string;
		lastDay: string;
	}>({
		resolver: zodResolver(schema),
		defaultValues: formDefaultValues,
		mode: 'onChange',
	});
	const prevDefaultValuesRef = useRef(formDefaultValues);
	const { isSubmitting, isValid, errors, isDirty } = formState;
	const values = watch();
	const isFormFilled = Boolean(values.firstDay && values.lastDay);
	const isSubmitDisabled = !isDirty || !isFormFilled || !isValid || isSubmitting;
	const firstDayError = errors.firstDay?.message;
	const lastDayError = errors.lastDay?.message;

	useEffect(() => {
		const hasChanged =
			prevDefaultValuesRef.current.firstDay !== formDefaultValues.firstDay ||
			prevDefaultValuesRef.current.lastDay !== formDefaultValues.lastDay;

		if (hasChanged) {
			prevDefaultValuesRef.current = formDefaultValues;
			reset(formDefaultValues);
		}
	}, [formDefaultValues, reset]);

	return (
		<Stack asChild gap={compact ? 2 : 4} {...otherProps} mt={compact ? 0 : 2}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Flex
					direction={compact ? 'row' : { base: 'column', lg: 'row' }}
					gap={compact ? 3 : 4}
					align={compact ? 'center' : { base: 'stretch', lg: 'end' }}
					justify={compact ? 'flex-start' : undefined}
					wrap={compact ? 'nowrap' : undefined}
				>
					<Field.Root
						invalid={Boolean(firstDayError)}
						flex={0}
						minW={compact ? '160px' : '320px'}
						alignSelf='center'
						display={compact ? 'flex' : undefined}
						flexDirection={compact ? 'column' : undefined}
						justifyContent={compact ? 'center' : undefined}
					>
						{!compact && <Field.Label fontSize="sm">Pierwsze zajęcia</Field.Label>}
						<Input
							type="date"
							size={compact ? 'sm' : undefined}
							{...register('firstDay')}
						/>
						{!compact && <Field.ErrorText>{firstDayError}</Field.ErrorText>}
					</Field.Root>
					<Field.Root invalid={Boolean(lastDayError)} flex={0} minW={compact ? '160px' : '320px'} alignSelf='center'>
						{!compact && <Field.Label fontSize="sm">Ostatnie zajęcia</Field.Label>}
						<Input
							type="date"
							size={compact ? 'sm' : undefined}
							{...register('lastDay')}
						/>
						{!compact && <Field.ErrorText>{lastDayError}</Field.ErrorText>}
					</Field.Root>
					<Button
						size={compact ? 'sm' : undefined}
						marginTop={compact ? 0 : { base: 0, lg: '26px' }}
						alignSelf={compact ? 'center' : 'flex-start'}
						variant="solid"
						colorPalette="black"
						loading={isSubmitting}
						spinnerPlacement="start"
						type="submit"
						disabled={isSubmitDisabled}
					>
						{submitBtnText}
					</Button>
				</Flex>
			</form>
		</Stack>
	);
};
